import React, { useCallback } from "react";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { EntryTypeInput } from "guestlist/EntryTypeInput";
import { useOpenSidePanel } from "../SidePanel";
import { useQueryClient } from "@tanstack/react-query";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";

interface NewAccessTypeFormState {
  quantity: number;
  entryType: server.dto.GuestlistEntryType;
  customLabel: string;
}

interface Props {
  guestlistId: number;
  onCreated: () => void;
}

const NewAccessTypeSidePanel = (props: Props) => {
  const [formState, setFormState] = React.useState<NewAccessTypeFormState>({
    quantity: undefined,
    entryType: null,
    customLabel: "",
  });

  const queryClient = useQueryClient();

  const onChange = (newData: Partial<NewAccessTypeFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const submit = () => {
    return window.CoreApi.Guestlist.CreateGuestlistAccessType({
      guestlistId: props.guestlistId,
      quantity: formState.quantity,
      entryType: formState.entryType.value,
      entryTypeCustomName: formState.customLabel,
    });
  }

  const onSuccess = async () => {
    props.onCreated();
  }

  return <SidePanelNewSectionTemplate
    onSave={() => {
      onSuccess();
      queryClient.invalidateQueries(["guestlistConfiguration", props.guestlistId]);
    }}
    afterCreate="closeSidebar"
    title={RESX.Guestlist.List}
    id="new-guest-list-access-type-side-panel"
    trigger={undefined}
    submit={submit}
    sections={[{
      key: "new-guest-list-access-type-side-panel",
      type: "regular",
      hideTitle: true,
      title: RESX.Guestlist.List,
      fields: [{
        key: "entryType",
        label: RESX.GeneralLabels.Type,
        render: () => <EntryTypeInput
          value={{
            entryType: formState.entryType,
            customLabel: formState.customLabel
          }}
          validatorKey="entryType"
          onChange={(v) => onChange({
            entryType: v.entryType,
            customLabel: v.customLabel
          })}
        />
      }, {
        key: "quantity",
        label: RESX.GeneralLabels.Available,
        render: () => <RequiredNumberInput
          maxValue={999}
          value={formState.quantity}
          onChange={(quantity: number) => onChange({ quantity })}
          decimalPoints={0}
          canBeEmpty
          canBeNegative={false}
          validatorKey="pax"
          width={75}
          labelContent="PAX"
        />
      }]
    }]}
  />
}

export const useOpenNewAccessTypeSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewAccessTypeSidePanel = useCallback((guestlistId: number, onCreated: () => void) => {
    openSidePanel(
      <NewAccessTypeSidePanel guestlistId={guestlistId} onCreated={onCreated} />,
      "new-access-type-side-panel",
      "medium"
    );
  }, [openSidePanel]);

  return openNewAccessTypeSidePanel;
}
